import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from "./authService";

// Query keys
export const adminVendorServiceKeys = {
  all: ['admin', 'vendor-services'],
  lists: () => [...adminVendorServiceKeys.all, 'list'],
  list: (filters) => [...adminVendorServiceKeys.lists(), { filters }],
  details: () => [...adminVendorServiceKeys.all, 'detail'],
  detail: (id) => [...adminVendorServiceKeys.details(), id],
  statistics: () => [...adminVendorServiceKeys.all, 'statistics'],
};

// API functions
const adminVendorServiceApi = {
  // Get all vendor services for admin
  getAll: async (params = {}) => {
    const response = await api.get('/admin/vendor-services', { params });
    return response.data;
  },

  // Get vendor service statistics
  getStatistics: async () => {
    const response = await api.get('/admin/vendor-services/statistics');
    return response.data;
  },

  // Toggle service active status
  toggleStatus: async (id) => {
    const response = await api.put(`/admin/vendor-services/${id}/toggle-status`);
    return response.data;
  },

  // Toggle service featured status
  toggleFeatured: async (id) => {
    const response = await api.put(`/admin/vendor-services/${id}/toggle-featured`);
    return response.data;
  },

  // Delete vendor service
  delete: async (id) => {
    const response = await api.delete(`/admin/vendor-services/${id}`);
    return response.data;
  },
};

// React Query hooks
export const useAdminVendorServices = (params = {}, options = {}) => {
  return useQuery({
    queryKey: adminVendorServiceKeys.list(params),
    queryFn: () => adminVendorServiceApi.getAll(params),
    ...options,
  });
};

export const useAdminVendorServiceStatistics = (options = {}) => {
  return useQuery({
    queryKey: adminVendorServiceKeys.statistics(),
    queryFn: adminVendorServiceApi.getStatistics,
    ...options,
  });
};

export const useToggleVendorServiceStatus = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: adminVendorServiceApi.toggleStatus,
    onSuccess: (data, serviceId) => {
      // Invalidate and refetch vendor services
      queryClient.invalidateQueries({ queryKey: adminVendorServiceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: adminVendorServiceKeys.statistics() });
      
      options.onSuccess?.(data);
    },
    onError: (error) => {
      options.onError?.(error);
    }
  });
};

export const useToggleVendorServiceFeatured = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: adminVendorServiceApi.toggleFeatured,
    onSuccess: (data, serviceId) => {
      // Invalidate and refetch vendor services
      queryClient.invalidateQueries({ queryKey: adminVendorServiceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: adminVendorServiceKeys.statistics() });
      
      options.onSuccess?.(data);
    },
    onError: (error) => {
      options.onError?.(error);
    }
  });
};

export const useDeleteVendorService = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: adminVendorServiceApi.delete,
    onSuccess: (data, serviceId) => {
      // Invalidate and refetch vendor services
      queryClient.invalidateQueries({ queryKey: adminVendorServiceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: adminVendorServiceKeys.statistics() });
      
      options.onSuccess?.(data);
    },
    onError: (error) => {
      options.onError?.(error);
    }
  });
};
